import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { TFunction } from "i18next";
import type { QueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import {
  voteForCandidateElectionsVotePost,
  type CityHallStateResponse,
} from "../../client";
import type { UserData } from "../../models/user";
import { handleApiError } from "../../utils/net/apiError";
import type { InsufficientCurrencyError } from "../../ui/modals/InsufficientPearlsModal";
import { impactOccurred, notificationOccurred } from "../../utils/telegram/hapticFeedback";

export interface UseCityHallVotingParams {
  getSelectedCandidateId: () => number | null;
  playVoteSuccess: () => void;
  queryClient: QueryClient;
  refetch: () => Promise<unknown>;
  setError: Dispatch<SetStateAction<string | null>>;
  setInsufficientCurrencyError: Dispatch<SetStateAction<InsufficientCurrencyError | null>>;
  state: CityHallStateResponse | undefined;
  t: TFunction;
  triggerConfetti: () => void;
  userData: UserData | undefined;
}

export interface UseCityHallVotingResult {
  handleVoteClick: (candidateId: number) => void;
  handleStarsVote: (candidateId: number) => Promise<void>;
  handleVote: () => Promise<void>;
  starsVotingCandidateId: number | null;
}

export function useCityHallVoting({
  getSelectedCandidateId,
  playVoteSuccess,
  queryClient,
  refetch,
  setError,
  setInsufficientCurrencyError,
  state,
  t,
  triggerConfetti,
  userData,
}: UseCityHallVotingParams): UseCityHallVotingResult {
  // Track local pearl balance for optimistic deduction (prevents over-voting)
  const localPearlBalanceRef = useRef<number | null>(null);
  // Sync from server data when it changes
  useEffect(() => {
    if (userData?.currency_balance != null) {
      localPearlBalanceRef.current = userData.currency_balance;
    }
  }, [userData?.currency_balance]);

  // In-flight vote tracking — prevents parallel duplicate API calls
  const voteInFlightRef = useRef(false);

  // One-tap vote: cast 1 vote for pearls (optimistic — instant UI, async API)
  const handleVoteClick = useCallback((candidateId: number) => {
    if (!state) return;
    const voteCost = state.vote_price_pearls;

    // Check local balance before sending
    if (localPearlBalanceRef.current !== null && localPearlBalanceRef.current < voteCost) {
      notificationOccurred("error");
      setInsufficientCurrencyError({
        error_type: "insufficient_currency",
        required_pearls: voteCost,
        available_pearls: localPearlBalanceRef.current,
        required_stars: null,
        available_stars: null,
      });
      return;
    }

    // Block if previous vote is still in-flight
    if (voteInFlightRef.current) return;

    setError(null);

    // Deduct locally to prevent rapid over-spending
    if (localPearlBalanceRef.current !== null) {
      localPearlBalanceRef.current -= voteCost;
    }

    // Instant feedback
    impactOccurred("medium");
    playVoteSuccess();

    // Optimistic cache update — increment vote count + deduct balance immediately
    queryClient.setQueryData<CityHallStateResponse>(["cityhall-state"], (old) => {
      if (!old) return old;
      return {
        ...old,
        candidates: (old.candidates ?? []).map((c) =>
          c.id === candidateId ? { ...c, votes_count: c.votes_count + 1 } : c
        ),
      };
    });
    queryClient.setQueryData<UserData>(["userData"], (old) => {
      if (!old) return old;
      return { ...old, currency_balance: (old.currency_balance ?? 0) - voteCost };
    });

    // Fire API (track in-flight state)
    voteInFlightRef.current = true;
    voteForCandidateElectionsVotePost({
      body: {
        candidate_id: candidateId,
        payment_method: 'pearls',
        votes_count: 1,
      },
      throwOnError: true,
    }).then(({ data: response }) => {
      if (response.candidate) {
        refetch(); // Refresh vote_history for all candidates
        posthog.capture('election_action', {
          action_type: 'vote',
          currency: 'pearls',
          votes_count: 1,
          price_per_vote: state.vote_price_pearls,
          total_price: state.vote_price_pearls,
        });
      }
    }).catch((err: unknown) => {
      console.error('[CityHallScreen] Error voting:', err);
      const apiErr = handleApiError(err, { logLabel: 'vote' });
      // Rollback local balance
      if (localPearlBalanceRef.current !== null) {
        localPearlBalanceRef.current += voteCost;
      }
      // Rollback optimistic updates (votes + balance)
      queryClient.setQueryData<UserData>(["userData"], (old) => {
        if (!old) return old;
        return { ...old, currency_balance: (old.currency_balance ?? 0) + voteCost };
      });
      refetch();
      if (apiErr.errorType === 'insufficient_currency') {
        setInsufficientCurrencyError({
          error_type: 'insufficient_currency',
          required_pearls: apiErr.extra.required_pearls ?? null,
          available_pearls: apiErr.extra.available_pearls ?? null,
          required_stars: apiErr.extra.required_stars ?? null,
          available_stars: apiErr.extra.available_stars ?? null,
        });
        return;
      }
      notificationOccurred("error");
      setError(t("Failed to vote. Please try again."));
    }).finally(() => {
      voteInFlightRef.current = false;
    });
  }, [queryClient, playVoteSuccess, refetch, setError, setInsufficientCurrencyError, state, t]);

  // Stars bulk vote: 100 votes for 99 Stars — opens Telegram invoice
  const [starsVotingCandidateId, setStarsVotingCandidateId] = useState<number | null>(null);
  const handleStarsVote = useCallback(async (candidateId: number) => {
    if (starsVotingCandidateId) return;
    setStarsVotingCandidateId(candidateId);
    setError(null);
    try {
      const { data: response } = await voteForCandidateElectionsVotePost({
        body: {
          candidate_id: candidateId,
          payment_method: 'stars',
          votes_count: 100,
        },
        throwOnError: true,
      });
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice && response.invoice_link) {
        tg.openInvoice(response.invoice_link, (status: string) => {
          if (status === "paid") {
            refetch();
            queryClient.invalidateQueries({ queryKey: ['userData'] });
            triggerConfetti();
            posthog.capture('election_action', {
              action_type: 'vote',
              currency: 'stars',
              votes_count: 100,
            });
          }
          setStarsVotingCandidateId(null);
        });
      } else {
        setStarsVotingCandidateId(null);
      }
    } catch (err: unknown) {
      console.error('[CityHallScreen] Error creating Stars vote invoice:', err);
      handleApiError(err, { logLabel: 'voteStars' });
      notificationOccurred("error");
      setError(t("Failed to vote. Please try again."));
      setStarsVotingCandidateId(null);
    }
  }, [starsVotingCandidateId, refetch, queryClient, setError, triggerConfetti, t]);

  // Legacy vote handler for vote_count screen (still used by that UI)
  const handleVote = async () => {
    const selectedCandidateId = getSelectedCandidateId();
    if (!selectedCandidateId) return;
    await handleVoteClick(selectedCandidateId);
  };

  return {
    handleVoteClick,
    handleStarsVote,
    handleVote,
    starsVotingCandidateId,
  };
}
